import React from 'react'
import { Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { LockGlyph12Icon } from 'assets/icons'
import InitiativeDefaultThumbnail from 'src/sites/kits/Initiative/components/DefaultThumbnail'
import { Title } from 'src/sites/kits/UI'

const EventListItemRow = ({
   borderColor,
   coverMedia,
   name,
   numAttendees,
   linkTo,
   location,
   pastEvent,
   parentInitiativeName,
   parentInitiativePrivacy,
   privacy,
   startDate,
   startTime,
   textColor,
   textSize,
   virtual
}) => {
   const hasParentInitiative = parentInitiativeName && parentInitiativePrivacy

   // We want to use the more restrictive privacy between the event and its parent (if it has one)
   const hasRestrictedParentInitiative = hasParentInitiative &&
      ['admin', 'draft', 'private'].includes(parentInitiativePrivacy)

   const applicablePrivacy = hasRestrictedParentInitiative && privacy === 'inherit'
      ? parentInitiativePrivacy
      : privacy

   return (
      <Link to={linkTo}>
         <View
            display="flex"
            alignItems="center"
            padding="10px 0px"
            borderBottom={`1px solid ${borderColor}`}>
            <View
               flex="0 0 auto"
               width="35px"
               height="35px"
               position="relative"
               backgroundColor="#eee"
               backgroundImage={coverMedia && `url(${coverMedia?.file.thumbnailUrlW480})`}
               backgroundSize="cover"
               backgroundPosition="bottom center"
               backgroundRepeat="no-repeat"
               borderRadius="6px"
               marginRight="20px">
               {!coverMedia && (
                  <InitiativeDefaultThumbnail
                     highlightColor="transparent"
                     iconColor="#ccc"
                     iconName="event"
                     iconSize="16px"
                  />
               )}
            </View>
            <View display="flex" flex="1 1 auto" flexFlow="column">
               <View flex="1 1 auto" opacity="0.8">
                  <Title size="xs" color={textColor}>
                     {name}
                  </Title>
               </View>
               <View display="flex" alignItems="center" flex="0 0 auto">
                  {['admin', 'draft', 'private'].includes(applicablePrivacy) && (
                     <>
                        {applicablePrivacy === 'private' && (
                           <View
                              position="relative"
                              top="-2px"
                              marginRight="5px"
                              opacity="0.5">
                              <LockGlyph12Icon width="7px" height="7px" color={textColor} />
                           </View>
                        )}
                        <View
                           flex="0 0 auto"
                           opacity={applicablePrivacy === 'private' ? '0.5' : '1'}>
                           <Text
                              size="1.5"
                              color={applicablePrivacy === 'private' ? textColor : '#ba723f'} transform="capitalize">
                              {applicablePrivacy !== 'admin' && applicablePrivacy}
                              {applicablePrivacy === 'admin' && 'Admin-only'}
                           </Text>
                        </View>
                        {((virtual || location || startDate) && (
                           <View padding="0px 5px" opacity="0.4">
                              <Text size="1.5" color={textColor}>—</Text>
                           </View>
                        ))}
                     </>
                  )}
                  <View flex="0 0 auto" wi2dth="180px[c-f]" opacity={!virtual && '0.5'}>
                     <Text size="1.5" color={virtual ? '#eb8044' : textColor}>
                        {virtual && 'Online'}
                        {!virtual && (location.city || location.state) && `${location.city}${location.city && location.state && ', '}${location.state}`}
                     </Text>
                  </View>
                  {(virtual || location.city || location.state) && startDate && (
                     <View padding="0px 5px" opacity="0.4">
                        <Text size="1.5" color={textColor}>—</Text>
                     </View>
                  )}
                  <View flex="0 0 auto" opacity="0.5">
                     <Text size="1.5" color={textColor}>
                        {startDate}
                        {startTime && ` at ${startTime}`}
                     </Text>
                  </View>
               </View>
            </View>
            {numAttendees > 5 && (
               <View flex="0 0 auto" display="none[a-b] block[c-f]" opacity="0.5">
                  <Text size="1.5" color={textColor}>
                     {`${numAttendees} people ${pastEvent ? 'attended' : 'attending'}`}
                  </Text>
               </View>
            )}
         </View>
      </Link>
   )
}

EventListItemRow.propTypes = {
   borderColor: PropTypes.string.isRequired,
   coverMedia: PropTypes.object,
   name: PropTypes.string.isRequired,
   linkTo: PropTypes.string.isRequired,
   location: PropTypes.object,
   numAttendees: PropTypes.number.isRequired,
   parentInitiativeName: PropTypes.string,
   parentInitiativePrivacy: PropTypes.oneOf(['admin', 'draft', 'private', 'inherit']),
   pastEvent: PropTypes.bool,
   privacy: PropTypes.string.isRequired,
   startDate: PropTypes.string,
   startTime: PropTypes.string,
   textColor: PropTypes.string.isRequired,
   textSize: PropTypes.string.isRequired,
   virtual: PropTypes.bool.isRequired
}

EventListItemRow.defaultProps = {
   coverMedia: undefined,
   location: undefined,
   parentInitiativeName: undefined,
   parentInitiativePrivacy: undefined,
   pastEvent: false,
   startDate: undefined,
   startTime: undefined
}

export default EventListItemRow
